/**
 * KRIMA Combo Cart Guard — assets/krima-combo-cart-guard.js
 * Versión: 5.2.1
 *
 * FIX v5.2.1 — findEl() podía afectar el elemento equivocado:
 * El selector de respaldo "#CartItem-{idx}" ubicaba el <li> SOLO por posición
 * numérica, sin verificar su key. Justo después de eliminar un item los índices
 * de los items restantes cambian, así que ese selector podía apuntar al <li>
 * de OTRO item (ej: un fee que sí debía permanecer) y afectarlo por error.
 * Se quitó — todo selector en findEl() ahora depende del key real del item.
 *
 * Validado en pruebas (2026-09): con 2+ combos con sobrecosto en el carrito,
 * eliminar uno ya solo borra SU fee item, no todos — pendiente confirmar en
 * producción con tráfico real. Se removieron los console.log/warn de debug
 * temporal usados para diagnosticar esto (computeOrphanFeeItems, findEl).
 *
 * PENDIENTE: ocasionalmente el fee huérfano no desaparece visualmente de
 * inmediato (el borrado en el backend sí ocurre) — reportado más que todo al
 * eliminar del carrito productos no-combo o combos sin sobrecosto. No se ha
 * podido reproducir de forma consistente todavía.
 *
 * Estrategia de detección de huérfanos en tres capas:
 *   A) Interceptor de window.fetch
 *   B) Interceptor de XMLHttpRequest (Alpine.js puede usar XHR)
 *   C) Polling cada 500ms cuando hay fee items en el carrito
 *
 * FIX v5.1.0 — Huérfanos con 2+ combos en el carrito:
 * Antes, un fee item solo se limpiaba si NO quedaba NINGÚN combo item en el
 * carrito (ver isComboItem más abajo) — con 2+ combos, eliminar el que tenía
 * sobrecosto dejaba su fee item huérfano sin limpiar porque el OTRO combo
 * seguía presente. Ahora cada fee item se evalúa individualmente contra la
 * property "_combo_parent_key" (agregada por krima-combo-v2.js al crear el fee
 * item) que apunta al key del combo que lo originó. Fee items sin ese vínculo
 * (agregados antes de este fix, en carritos que ya estaban abiertos) caen al
 * comportamiento anterior como fallback — ver computeOrphanFeeItems().
 *
 * FIX v5.2.0 — Huérfano seguía visible tras eliminarse en el backend:
 * checkOrphans()/init() hacían location.reload() tras eliminar el fee item
 * huérfano vía /cart/update.js. El reload podía tardar o no verse reflejado de
 * inmediato, dejando el <li> del fee item visible aunque ya no existiera en el
 * carrito — con riesgo real de que el cliente pensara que igual se lo iban a
 * cobrar. Ahora, en vez de recargar la página, removeFeeItemsVisually() quita
 * el <li> del DOM directamente (buscándolo con findEl() antes de que el fee
 * item deje de existir en /cart.js) y dispara 'cart:refresh'/'cart:updated'
 * para que el tema resincronice subtotal/contador si los escucha — mismo
 * patrón que ya usa cart-sampling-kit-properties.liquid para cambios de
 * carrito que no vienen de un clic del usuario.
 *
 * INSTALACIÓN: antes de </body> en theme.liquid:
 *   <script src="{{ 'krima-combo-cart-guard.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  const FEE_PROP        = '_combo_fee';
  const PARENT_KEY_PROP = '_combo_parent_key';
  const GUARD_ATTR      = 'data-krima-fee-locked';

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  function isFeeItem(item) {
    return item.properties?.[FEE_PROP] === 'true';
  }

  function isComboItem(item) {
    return !isFeeItem(item) &&
      Object.keys(item.properties || {}).some(k => k.startsWith('Selección'));
  }

  async function getCart() {
    return window._krimaOriginalFetch('/cart.js').then(r => r.json());
  }

  // ─── Estado global ────────────────────────────────────────────────────────────

  const state = {
    feeLineKeys:    new Set(),
    internalUpdate: false,
  };

  // Guardar referencias originales ANTES de parchear
  window._krimaOriginalFetch = window.fetch;
  const OriginalXHR = window.XMLHttpRequest;

  // ─── Sincronización de keys ───────────────────────────────────────────────────

  async function syncKeys() {
    try {
      const cart = await getCart();
      state.feeLineKeys.clear();
      cart.items.forEach((item, i) => {
        if (isFeeItem(item)) {
          state.feeLineKeys.add(item.key);
          //state.feeLineKeys.add(String(i + 1));
          //COMENTADO PARA REVISIÓN DE ERROR DE CART DRAWER
        }
      });
    } catch (e) {}
  }

  // ─── Limpieza de huérfanos ───────────────────────────────────────────────────

  let orphanCheckPending = false;

  // Determina qué fee items ya no tienen razón de estar en el carrito.
  // - Fee item CON _combo_parent_key (creado por krima-combo-v2.js desde el fix):
  //   huérfano si ESE combo específico ya no está en el carrito.
  // - Fee item SIN _combo_parent_key (legacy, agregado antes del fix, o en un
  //   carrito que ya estaba abierto cuando se desplegó): fallback al
  //   comportamiento anterior — huérfano solo si no queda NINGÚN combo.
  function computeOrphanFeeItems(cart) {
    const feeItems = cart.items.filter(isFeeItem);
    if (!feeItems.length) return [];

    const comboKeys = new Set(cart.items.filter(isComboItem).map(i => i.key));

    return feeItems.filter(fee => {
      const parentKey = fee.properties?.[PARENT_KEY_PROP];
      if (parentKey) return !comboKeys.has(parentKey);
      return comboKeys.size === 0;
    });
  }

  async function removeFeeItems(feeItems) {
    const updates = {};
    feeItems.forEach(item => { updates[item.key] = 0; });

    state.internalUpdate = true;
    const r = await window._krimaOriginalFetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ updates }),
    });
    state.internalUpdate = false;
    return r;
  }

  // Quita del DOM los <li> de los fee items ya eliminados en el backend y avisa
  // al tema para que resincronice totales/contador — sin location.reload(). El
  // fee item ya no existe en /cart.js en este punto; findEl() solo puede ubicar
  // su <li> porque lo buscamos ANTES de llamar a removeFeeItems (ver checkOrphans
  // / init), con el "cart" todavía conteniendo ese item.
  function removeFeeItemsVisually(feeItems, cart) {
    feeItems.forEach(item => {
      state.feeLineKeys.delete(item.key);
      const el = findEl(item, cart);
      if (!el) return;
      el.style.transition = 'opacity 150ms ease, transform 150ms ease';
      el.style.opacity   = '0';
      el.style.transform = 'scale(0.98)';
      setTimeout(() => el.remove(), 160);
    });

    // Mismo patrón que cart-sampling-kit-properties.liquid: avisar al tema de un
    // cambio de carrito que no vino de un clic suyo, por si su store de Alpine
    // escucha estos eventos para refrescar subtotal/contador.
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'krima-cart-guard' } }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'krima-cart-guard' } }));
  }

  async function checkOrphans() {
    if (orphanCheckPending) return;
    orphanCheckPending = true;

    try {
      const cart           = await getCart();
      const orphanFeeItems = computeOrphanFeeItems(cart);
      if (!orphanFeeItems.length) { orphanCheckPending = false; return; }

      console.log('[KrimaCartGuard] Fee items huérfanos detectados — eliminando...', orphanFeeItems.map(i => i.key));

      const r = await removeFeeItems(orphanFeeItems);

      if (r.ok) {
        console.log('[KrimaCartGuard] Fee items huérfanos eliminados correctamente.');
        removeFeeItemsVisually(orphanFeeItems, cart);
      } else {
        console.warn('[KrimaCartGuard] No se pudieron eliminar los fee items huérfanos:', r.status);
      }
    } catch (e) {
      state.internalUpdate = false;
    }

    orphanCheckPending = false;
  }

  function scheduleOrphanCheck() {
    setTimeout(checkOrphans, 700);
  }

  // ─── A) Interceptor de fetch ──────────────────────────────────────────────────

  window.fetch = function (url, options = {}) {
    const urlStr = typeof url === 'string' ? url : (url?.url || '');

    if (!state.internalUpdate) {

      // Bloquear eliminación/modificación del fee item
      if (urlStr.includes('/cart/change') && isFeeTarget(options)) {
        console.warn('[KrimaCartGuard] Eliminación del cargo adicional bloqueada (fetch).');
        return window._krimaOriginalFetch('/cart.js');
      }

      if (urlStr.includes('/cart/update')) {
        const s = sanitizeUpdate(options);
        if (s.blocked) {
          console.warn('[KrimaCartGuard] Modificación del cargo adicional bloqueada (fetch).');
          return s.hasOther
            ? window._krimaOriginalFetch(url, { ...options, body: JSON.stringify(s.body) })
            : window._krimaOriginalFetch('/cart.js');
        }
      }

      // Detectar cambios en el carrito para buscar huérfanos
      if (urlStr.includes('/cart/change') || urlStr.includes('/cart/update')) {
        const promise = window._krimaOriginalFetch.call(this, url, options);
        promise.then(res => { if (res.ok) scheduleOrphanCheck(); }).catch(() => {});
        return promise;
      }
    }

    //CORRECCIÓN 1 PARA PROBLEMAS DEL DRAWER

    // Al final del bloque de /cart/change, antes del return final
    /*if (urlStr.includes('/cart/change') && !state.internalUpdate) {
      try {
        const body = JSON.parse(options?.body || '{}');
        if (body.id) {
          const correctedId = getUpdatedKey(body.id);
          if (correctedId !== body.id) {
            console.log('[KrimaCartGuard] Key corregido:', body.id, '→', correctedId);
            options = { ...options, body: JSON.stringify({ ...body, id: correctedId }) };
          }
        }
      } catch(e) {}
    }*/

    //FIN CORRECCIÓN 1

    return window._krimaOriginalFetch.call(this, url, options);
  };

  // ─── B) Interceptor de XMLHttpRequest ─────────────────────────────────────────

  window.XMLHttpRequest = function () {
    const xhr     = new OriginalXHR();
    let   xhrUrl  = '';

    const originalOpen = xhr.open.bind(xhr);
    xhr.open = function (method, url, ...rest) {
      xhrUrl = String(url || '');
      return originalOpen(method, url, ...rest);
    };

    const originalSend = xhr.send.bind(xhr);
    xhr.send = function (body) {
      const isCartChange = xhrUrl.includes('/cart/change') || xhrUrl.includes('/cart/update');

      if (isCartChange && !state.internalUpdate) {
        // Verificar si apunta a un fee item (body como string o FormData)
        if (xhrUrl.includes('/cart/change') && isFeeTargetFromBody(body)) {
          console.warn('[KrimaCartGuard] Eliminación del cargo adicional bloqueada (XHR).');
          // No enviar — devolver respuesta vacía válida del carrito
          window._krimaOriginalFetch('/cart.js')
            .then(r => r.text())
            .then(text => {
              Object.defineProperty(xhr, 'responseText', { value: text, writable: true });
              Object.defineProperty(xhr, 'status',       { value: 200, writable: true });
              xhr.dispatchEvent(new Event('load'));
            });
          return;
        }

        // Escuchar respuesta para detectar huérfanos
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) scheduleOrphanCheck();
        });
      }

      return originalSend(body);
    };

    return xhr;
  };

  // Heredar prototype para compatibilidad
  window.XMLHttpRequest.prototype = OriginalXHR.prototype;
  Object.keys(OriginalXHR).forEach(k => {
    try { window.XMLHttpRequest[k] = OriginalXHR[k]; } catch (e) {}
  });

  // ─── C) Polling de seguridad ──────────────────────────────────────────────────
  // Corre cada 500ms solo cuando hay fee items, como red de seguridad principal.
  // El tema elimina el combo por una vía que no pasa por nuestros interceptores
  // de fetch/XHR, así que el polling es el mecanismo que realmente detecta el
  // huérfano. 500ms para no dar tiempo a que el cliente avance al checkout
  // antes de que el fee huérfano sea detectado y eliminado.

  setInterval(async () => {
    if (state.internalUpdate) return;
    if (!state.feeLineKeys.size) return;
    await checkOrphans();
  }, 500);

  // ─── Helpers de detección ────────────────────────────────────────────────────

  function isFeeTarget(options) {
    return isFeeTargetFromBody(options?.body);
  }

  //se comenta el siguiente bloque de función para revisión del error en el cart drawer, función "corregida" en el siguiente bloque

  /*function isFeeTargetFromBody(body) {
    try {
      let id = '', line = '';
      if (body instanceof FormData) {
        id   = body.get('id') || '';
        line = String(body.get('line') || '');
      } else if (typeof body === 'string') {
        // Puede ser JSON o query string
        if (body.trim().startsWith('{')) {
          const b = JSON.parse(body);
          id   = b.id || b.line_item_key || '';
          line = String(b.line || '');
        } else {
          const p = new URLSearchParams(body);
          id   = p.get('id') || '';
          line = String(p.get('line') || '');
        }
      }
      return state.feeLineKeys.has(id) || state.feeLineKeys.has(line);
    } catch (e) { return false; }
  }*/

  function isFeeTargetFromBody(body) {
      try {
        let id = '';
        if (body instanceof FormData) {
          id = body.get('id') || body.get('line_item_key') || '';
        } else if (typeof body === 'string') {
          if (body.trim().startsWith('{')) {
            const b = JSON.parse(body);
            id = b.id || b.line_item_key || b.key || '';
          } else {
            const p = new URLSearchParams(body);
            id = p.get('id') || p.get('line_item_key') || '';
          }
        }
        return state.feeLineKeys.has(id);
      } catch (e) { return false; }
  }

  /*function isFeeTargetFromBody(body) {
    try {
      let id = '';
      if (typeof body === 'string') {
        if (body.trim().startsWith('{')) {
          const b = JSON.parse(body);
          id = b.id || b.line_item_key || b.key || '';
        }
      }
      console.log('[KrimaCartGuard DEBUG] id detectado:', id, '| feeKeys:', [...state.feeLineKeys]);
      return state.feeLineKeys.has(id);
    } catch (e) { return false; }
  }*/

  function sanitizeUpdate(options) {
    try {
      const body = JSON.parse(options?.body || '{}');
      if (!body.updates) return { blocked: false };
      let blocked = false, hasOther = false;
      const clean = {};
      Object.entries(body.updates).forEach(([k, v]) => {
        if (state.feeLineKeys.has(k)) { blocked = true; }
        else { clean[k] = v; hasOther = true; }
      });
      return blocked ? { blocked, hasOther, body: { ...body, updates: clean } } : { blocked: false };
    } catch (e) { return { blocked: false }; }
  }

  // ─── UI: ocultar controles del fee item ───────────────────────────────────────

  function initUI() {
    runUI();
    new MutationObserver(() => runUI())
      .observe(document.body, { childList: true, subtree: true });
    ['cart:refresh', 'cart:updated', 'cart:open'].forEach(evt =>
      document.addEventListener(evt, () => setTimeout(runUI, 200))
    );
  }

  async function runUI() {
    try {
      const cart     = await getCart();
      const feeItems = cart.items.filter(isFeeItem);
      feeItems.forEach(item => {
        const el = findEl(item, cart);
        if (el && !el.hasAttribute(GUARD_ATTR)) lockEl(el);
      });
    } catch (e) {}
  }

  function findEl(item, cart) {
    const key = item.key;
    const idx = cart.items.indexOf(item) + 1;

    // OJO: NO incluir un selector que identifique el elemento solo por índice
    // numérico (ej: "#CartItem-{idx}") sin el key — justo después de eliminar
    // un item los índices cambian, y ese tipo de selector puede terminar
    // apuntando al <li>/fila de OTRO item distinto y afectarlo por error.
    // Todo selector acá debe depender del key real del item, no de su posición.
    for (const sel of [
      `[data-line-item-key="${key}"]`,
      `#CartItem-${idx}-${key}`,
    ]) {
      const el = document.querySelector(sel);
      if (el) return el.closest('li') || el;
    }

    for (const li of document.querySelectorAll('li[x-data]')) {
      if (li.getAttribute('x-data')?.includes(key)) return li;
    }

    return null;
  }

  function lockEl(el) {
    el.setAttribute(GUARD_ATTR, 'true');

    el.querySelectorAll('a[href*="quantity=0"], a[href*="/cart/change"]')
      .forEach(a => { (a.closest('p') || a).style.cssText = 'display:none!important'; });

    const qBlock = el.querySelector('[x-data="CartItemQuantity"]');
    if (qBlock) {
      qBlock.querySelectorAll('button[aria-label="−"], button[aria-label="+"]')
        .forEach(btn => {
          btn.disabled = true;
          btn.style.cssText = 'pointer-events:none!important;opacity:0.35!important;cursor:not-allowed!important';
        });

      const input = qBlock.querySelector('input[name="updates[]"], input[type="number"]');
      if (input) {
        input.readOnly = true;
        input.style.cssText = 'pointer-events:none!important;opacity:0.6!important';
        input.addEventListener('change', e => e.stopImmediatePropagation(), { capture: true });
        input.addEventListener('keyup',  e => e.stopImmediatePropagation(), { capture: true });
      }

      qBlock.addEventListener('remove', e => {
        e.stopImmediatePropagation();
        e.preventDefault();
      }, { capture: true });
    }

    console.log('[KrimaCartGuard] Fee item bloqueado visualmente.');
  }

  // ─── Bootstrap ───────────────────────────────────────────────────────────────

  async function init() {
    // Combinar syncKeys + checkOrphans en una sola llamada a /cart.js
    // para minimizar la ventana de tiempo en la que el reload del tema
    // puede interrumpir nuestra limpieza de huérfanos.
    try {
      const cart = await getCart();

      // Sincronizar keys
      state.feeLineKeys.clear();
      cart.items.filter(isFeeItem).forEach(item => state.feeLineKeys.add(item.key));

      // Verificar huérfanos inmediatamente con los datos ya obtenidos
      const orphanFeeItems = computeOrphanFeeItems(cart);
      if (orphanFeeItems.length) {
        console.log('[KrimaCartGuard] Fee items huérfanos detectados — eliminando...', orphanFeeItems.map(i => i.key));

        const r = await removeFeeItems(orphanFeeItems);

        if (r.ok) {
          console.log('[KrimaCartGuard] Fee items huérfanos eliminados correctamente.');
          removeFeeItemsVisually(orphanFeeItems, cart);
        } else {
          console.warn('[KrimaCartGuard] No se pudieron eliminar los fee items huérfanos:', r.status);
        }
      }
    } catch (e) {
      state.internalUpdate = false;
    }

    initUI();
    console.log('[KrimaCartGuard] v5.2 activo. Fee keys:', [...state.feeLineKeys]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.KrimaCartGuard = {
    state, syncKeys, checkOrphans, runUI,
    cleanOrphans: async () => {
      state.internalUpdate = true;
      const cart    = await getCart();
      const updates = {};
      cart.items.filter(isFeeItem).forEach(i => { updates[i.key] = 0; });
      if (!Object.keys(updates).length) {
        state.internalUpdate = false;
        console.log('[KrimaCartGuard] No hay fee items huérfanos.');
        return;
      }
      await window._krimaOriginalFetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      state.internalUpdate = false;
      location.reload();
    },
  };


  //CORRECCIÓN 2 PARA EL PROBLEMA DEL DRAWER
  // ─── Fix: corregir key stale antes de enviar a Shopify ───────────────────────
  /*function getUpdatedKey(staleId) {
    // Buscar en el DOM el line item que tiene ese variantId
    const variantId = staleId.split(':')[0];
    
    // Buscar el li con data-line-item-key cuyo key empiece con ese variantId
    const lineItems = document.querySelectorAll('[data-line-item-key]');
    for (const li of lineItems) {
      const currentKey = li.dataset.lineItemKey;
      if (currentKey && currentKey.startsWith(variantId + ':')) {
        return currentKey;
      }
    }
    
    // Si no encontramos, devolver el original
    return staleId;
  }*/
  //FIN CORRECCIÓN 2

})();